import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ITooltipAngularComp } from '@ag-grid-community/angular';
import { ColDef, ColGroupDef, Column, ITooltipParams } from '@ag-grid-community/core';
import * as _ from 'lodash';
import { DomSanitizer } from '@angular/platform-browser';
import { getFormattedDateIncludeTime, isString } from '@frontend-site/shared/utils';

@Component({
    selector: 'grid-tooltip',
    template: `
        <div *ngIf="tooltip && !isHTML" class="grid-tooltip">
            <span>{{ tooltip }}</span>
        </div>
        <div *ngIf="tooltip && isHTML" class="grid-tooltip">
            <span [innerHTML]="getTextAsHtml()"></span>
        </div>
    `,
    styleUrls: ['./grid-tooltip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GridTooltipComponent implements ITooltipAngularComp {
    public tooltip: any;
    public isHTML?: boolean;

    constructor(private domSanitizer: DomSanitizer) {}

    agInit(params: ITooltipParams): void {
        this.initTooltip(params);
        this.handleLinkIfExists();
        if (!this.isHTML) {
            if ((params.colDef as ColDef).filter === 'agDateColumnFilter' && params.location === 'cell') {
                // used in history grid
                this.tooltip = getFormattedDateIncludeTime(params.value);
            } else {
                this.handleOtherSpecialCells(params);
            }
        }
    }

    private initTooltip(params: ITooltipParams): void {
        const colDef = params.colDef as ColDef | undefined;
        this.isHTML = colDef?.tooltipComponentParams?.isHTML;
        if (!params.data) {
            this.tooltip = _.escape(params.value); // relevant for headers
        } else if (colDef && isColDefWithTooltipValueGetter(colDef)) {
            this.tooltip = colDef.tooltipValueGetter(params);
        } else {
            const colId = (params.column as Column).getColId();
            if (colId === 'text' && !!params.data.textInfo) {
                this.tooltip = params.data?.textInfo;
            } else {
                this.tooltip = params.data[colId];
            }
        }
    }

    private handleLinkIfExists() {
        const linkSeparatorIndex = isString(this.tooltip) ? this.tooltip.indexOf('[$%^]') : -1;
        if (linkSeparatorIndex >= 0) {
            this.tooltip = this.tooltip.substring(0, linkSeparatorIndex);
        }
    }

    private handleOtherSpecialCells(params: ITooltipParams) {
        switch ((params.column as Column).getColDef()?.cellRenderer) {
            case 'richTextRenderer':
                this.tooltip = new DOMParser().parseFromString(this.tooltip, 'text/html').documentElement.textContent;
                break;
            case 'buttonRenderer':
                this.tooltip = params?.data?.serverRendering ? '' : this.tooltip;
                break;
            case 'linkableIconRenderer':
                this.tooltip = '';
                break;
            case 'linkDialogRenderer':
                this.tooltip = '';
                break;
            case 'iconTextRenderer': // tooltips are inside icons map, handled in IconTextRendererComponent
                this.tooltip = '';
                break;
            case 'statusSubLabelRenderer':
                const cellRendererParams = params.colDef ? (params.colDef as ColDef).cellRendererParams : undefined;
                if (!!cellRendererParams && !!cellRendererParams.getTooltipValue) {
                    this.tooltip = '';
                }
                break;
        }
    }

    public getTextAsHtml() {
        const styleRegex = new RegExp('(?=style=").*?(?=>)');
        return this.domSanitizer.bypassSecurityTrustHtml(this.tooltip.replace(styleRegex, ''));
    }
}

function isColDefWithTooltipValueGetter(
    definition: ColDef | ColGroupDef
): definition is ColDef & { tooltipValueGetter: NonNullable<ColDef['tooltipValueGetter']> } {
    return !!(definition as ColDef)?.tooltipValueGetter;
}
